// Public-side hook. Fetches /api/feature-flags once on mount, returns a
// keyed map so a section can ask `flags['quiz']?.status === 'live'`
// without iterating an array.
//
// While loading or on error, every flag is treated as 'live' — graceful
// default that keeps the dashboard rendering even if the flags endpoint
// is briefly unreachable.

import { useEffect, useState } from 'react';
import type { FeatureFlagRow } from '../db/types';

export type FlagsMap = Record<string, FeatureFlagRow>;

interface ApiResponse {
  flags: FeatureFlagRow[];
  fetchedAt: string;
}

export function useFeatureFlags(): FlagsMap {
  const [flags, setFlags] = useState<FlagsMap>({});

  useEffect(() => {
    let cancelled = false;
    fetch('/api/feature-flags', { cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<ApiResponse>) : null))
      .then((data) => {
        if (cancelled || !data) return;
        const map: FlagsMap = {};
        for (const f of data.flags) map[f.section_key] = f;
        setFlags(map);
      })
      .catch(() => {
        // Fail open — graceful default leaves every section live.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return flags;
}
